import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { LogService } from './log.service';
import { MessagesService } from './messages.service';
import { CommonService } from './common.service';
import { User } from '../models/users/User';
import { Observable } from 'rxjs/Observable';
import { catchError, tap } from 'rxjs/operators';
import { PageOption } from '../models/common/pageOption';
import { KeyValuePair } from '../models/common/keyValuePair';
import { UserFilter } from '../models/users/userFilter';
import { CacheService } from './cache.service';
import { CACHE_KEYS } from '../contants/CACHE_KEYS';
import { of } from 'rxjs/observable/of';
import { RequestResult } from '../models/common/requestResult';
import { PageOptionResult } from '../models/common/pageOptionResult';
import { SalaryReport } from '../models/salaries/salaryReport';
import { Salary } from '../models/salaries/salary';
import { ErrorCollection } from '../models/common/errorCollection';
import { PaginatedList } from './../models/common/paginatedList';

@Injectable()
export class UserService {

	private origin: string = environment.API_BASE_URL;

	constructor(
		private _http: HttpClient,
		private _logService: LogService,
		private _messagesService: MessagesService,
		private _common: CommonService,
		private _cacheService: CacheService
	) { }

	getUser(id: number): Observable<User> {
		return this._http.get<User>(`${this.origin}/api/users/${id}`)
			.pipe(
				tap(x => this.log(`getUser: id=${id}`)),
				catchError(this._logService.handleError<User>('getUser'))
			);
	}

	getCurrentUser(): Observable<User> {
		return this._http.get<User>(`${this.origin}/api/users/current`)
			.pipe(
				tap(x => this.log(`getCurrentUser`)),
				catchError(this._logService.handleError<User>('getCurrentUser'))
			);
	}

	search(filter: PageOption<UserFilter>): Observable<PaginatedList<User>> {
		let param = this._common.objectToQueryParam(filter);
		return this._http.get<PaginatedList<User>>(`${this.origin}/api/users${param}`)
			.pipe(
				tap(x => this.log(`userService.search param = ${param}`)),
				catchError(this._logService.handleError<PaginatedList<User>>('userService.search'))
			);
	}

	getUsersDropdownList(cacheKey: string, role?: string): Observable<KeyValuePair[]> {
		let value = this._cacheService.get(cacheKey)
		if (value != null) {
			return of(value);
		}

		if (role == undefined || role == null) {
			role = '';
		}

		return this._http.get<KeyValuePair[]>(`${this.origin}/api/menus/users?role=${role}`)
			.pipe(
				tap(x => {
					this.log(`getUsersDropdownList role=${role}`);
					this._cacheService.add(cacheKey, x, 180);
				}),
				catchError(this._logService.handleError<KeyValuePair[]>('getUsersDropdownList'))
			);
    }
    
    getUsersWithRoles(roles: string[]): Observable<User[]> {
		return this._http.get<User[]>(`${this.origin}/api/users?roles${roles.join(',')}`)
			.pipe(
				tap(x => this.log(`getUsersWithRoles roles = ${roles.join(',')}`)),
				catchError(this._logService.handleError<User[]>('getUsersWithRoles'))
			);
	}

	getMonthlyUserSalary(userId: number): Observable<Salary> {
		return this._http.get<Salary>(`${this.origin}/api/users/${userId}/salary`)
			.pipe(
				tap(x => this.log(`getMonthlyUserSalary: id=${userId}`)),
				catchError(this._logService.handleError<Salary>('getMonthlyUserSalary'))
			);
	}

	addUser(user: User): Observable<RequestResult<User>> {
		return this._http.post<RequestResult<User>>(`${this.origin}/api/users`, JSON.stringify(user))
			.pipe(
				tap(x => this.log(`addUser: id=${user.id}`)),
				catchError(this._logService.handleError<RequestResult<User>>('addUser'))
			);
	}

	updateUser(user: User): Observable<RequestResult<User>> {
		return this._http.put<RequestResult<User>>(`${this.origin}/api/users/${user.id}`, JSON.stringify(user))
			.pipe(
				tap(x => this.log(`updateUser: id=${user.id}`)),
				catchError(this._logService.handleError<RequestResult<User>>('updateUser'))
			);
	}

	deactivateUser(userId: number): Observable<ErrorCollection> {
		return this._http.delete<ErrorCollection>(`${this.origin}/api/users/${userId}`)
			.pipe(
				tap(x => this.log(`deactivateUser: id=${userId}`)),
				catchError(this._logService.handleError<ErrorCollection>('deactivateUser'))
			);
	}

	activateUser(userId: number): Observable<ErrorCollection> {
		return this._http.put<ErrorCollection>(`${this.origin}/api/users/${userId}/activate`, '')
			.pipe(
				tap(x => this.log(`activateUser: id=${userId}`)),
				catchError(this._logService.handleError<ErrorCollection>('activateUser'))
			);
	}

	///Changes password for current user, any user can only change its own password
	changePassword(oldPassword: string, newPassword: string): Observable<ErrorCollection> {
		var data = {
			oldPassword: oldPassword,
			newPassword: newPassword
		}
		return this._http.post<ErrorCollection>(`${this.origin}/api/users/password`, JSON.stringify(data))
			.pipe(
				tap(x => this.log(`changePassword`)),
				catchError(this._logService.handleError<ErrorCollection>('changePassword'))
			);
	}

	private log(message: string) {
		this._messagesService.add('UserService: ' + message);
	}
}



// WEBPACK FOOTER //
// ./src/app/services/user.service.ts